import React from 'react';
import * as R from 'ramda';
// helpers/constants
import * as G from '../../helpers';
// hooks
import { useElementSize } from '../../hooks';
// icons
import * as I from '../../svgs';
// ui
import { Span, Flex } from '../../ui';
//////////////////////////////////////////////////

const ITEM_ICON_GAP = 4;
const ITEM_ICON_SIZE = 16;
const OVERFLOW_BADGE_WIDTH = 28;

const greyBlueColor = G.getTheme('colors.greyBlue');
const overflowColor = G.getTheme('colors.dark.blue');
const overflowBackground = G.getTheme('colors.bgGrey');

const getTemperatureIcon = (smallIcons, iconSize) => {
  if (G.isTrue(smallIcons)) return I.temperatureSensor(greyBlueColor, 10, 15);

  if (G.isNotNil(iconSize)) return I.temperatureSensor(greyBlueColor, iconSize, iconSize);

  return I.temperatureSensor(greyBlueColor);
};

const ItemIcon = ({ title, children }) => (
  <Flex
    title={title}
    flexShrink={0}
    width={ITEM_ICON_SIZE}
    height={ITEM_ICON_SIZE}
    justifyContent='center'
  >
    {children}
  </Flex>
);

const TemperatureHigh = ({ value, iconSize, showText, smallIcons }) => (
  <Flex
    flexShrink={0}
    px={G.ifElse(showText, '4px', 0)}
    title={`${G.getWindowLocale('titles:temperature-high', 'Temperature High')}: ${value}`}
  >
    {getTemperatureIcon(smallIcons, iconSize)}
    {
      showText &&
      <Span ml='6px'>{value}</Span>
    }
  </Flex>
);

const TemperatureLow = ({
  iconSize,
  showText,
  smallIcons,
  temperatureLowDisplayValue,
  temperatureHighDisplayValue,
}) => {
  let title = `${G.getWindowLocale('titles:temperature-low', 'Temperature Low')}: ${temperatureLowDisplayValue}`;

  if (G.isNotNil(temperatureHighDisplayValue)) {
    title = `${title}; ${
      G.getWindowLocale('titles:temperature-high', 'Temperature High')}: ${temperatureHighDisplayValue}`;
  }

  return (
    <Flex
      title={title}
      flexShrink={0}
      px={G.ifElse(showText, '4px', 0)}
    >
      {getTemperatureIcon(smallIcons, iconSize)}
      {
        showText &&
        <Span ml='6px'>{temperatureLowDisplayValue}</Span>
      }
    </Flex>
  );
};

export const getVisibleItemIconsCount = (availableWidth, itemIconsCount) => {
  if (R.equals(itemIconsCount, 0)) return itemIconsCount;

  if (R.lte(availableWidth, 0)) return 0;

  const itemIconsWidth = (itemIconsCount * ITEM_ICON_SIZE) + ((itemIconsCount - 1) * ITEM_ICON_GAP);

  if (R.gte(availableWidth, itemIconsWidth)) return itemIconsCount;

  const availableIconsWidth = Math.max(
    0,
    availableWidth - OVERFLOW_BADGE_WIDTH - ITEM_ICON_GAP,
  );

  const visibleIconsCount = Math.floor(
    (availableIconsWidth + ITEM_ICON_GAP) / (ITEM_ICON_SIZE + ITEM_ICON_GAP),
  );

  return Math.max(0, Math.min(itemIconsCount - 1, visibleIconsCount));
};

export const ItemIcons = ({
  width,
  iconGap,
  anyTapa,
  iconSize,
  showText,
  anyPharma,
  smallIcons,
  anyHazardous,
  temperatureLow,
  justifyContent,
  temperatureHigh,
  anyHighSecurity,
  onOverflowClick,
  collapseOverflow,
}) => {
  let temperatureLowValue;
  let temperatureLowDisplayValue;
  let temperatureHighValue;
  let temperatureHighDisplayValue;

  if (G.isImperialUomSystem()) {
    temperatureLowValue = G.toFixed(G.fromCelsiusToFahrenheit(temperatureLow), 0);
    temperatureLowDisplayValue = `${temperatureLowValue}F`;
    temperatureHighValue = G.toFixed(G.fromCelsiusToFahrenheit(temperatureHigh), 0);
    temperatureHighDisplayValue = `${temperatureHighValue}F`;
  } else {
    temperatureLowValue = G.toFixed(temperatureLow, 0);
    temperatureLowDisplayValue = `${temperatureLowValue}C`;
    temperatureHighValue = G.toFixed(temperatureHigh, 0);
    temperatureHighDisplayValue = `${temperatureHighValue}C`;
  }

  const specialIconSize = G.ifElse(smallIcons, 14, R.or(iconSize, ITEM_ICON_SIZE));
  const [containerRef, containerSize] = useElementSize();

  const itemIcons = R.filter(Boolean, [
    G.isTrue(anyTapa) && (
      <ItemIcon key='tapa' title={G.getWindowLocale('titles:tapa', 'TAPA')}>
        {I.renderTapaIcon(null, specialIconSize, specialIconSize)}
      </ItemIcon>
    ),
    G.isTrue(anyHighSecurity) && (
      <ItemIcon key='highSecurity' title={G.getWindowLocale('titles:high-sequrity', 'High Sequrity')}>
        {I.renderHighSecurityIcon(null, specialIconSize, specialIconSize)}
      </ItemIcon>
    ),
    G.isTrue(anyPharma) && (
      <ItemIcon key='pharma' title={G.getWindowLocale('titles:pharma', 'Pharma')}>
        {I.renderPharmaIcon(null, specialIconSize, specialIconSize)}
      </ItemIcon>
    ),
    G.isTrue(anyHazardous) && (
      <ItemIcon key='hazardous' title={G.getWindowLocale('titles:hazardous', 'Hazardous')}>
        {I.renderHazardousIcon(null, specialIconSize, specialIconSize)}
      </ItemIcon>
    ),
    G.isNotNil(temperatureLow) && (
      <TemperatureLow
        key='temperature'
        iconSize={iconSize}
        showText={showText}
        smallIcons={smallIcons}
        temperatureLowDisplayValue={temperatureLowDisplayValue}
        temperatureHighDisplayValue={G.ifElse(R.isNil(temperatureHigh), null, temperatureHighDisplayValue)}
      />
    ),
    R.and(R.isNil(temperatureLow), G.isNotNil(temperatureHigh)) && (
      <TemperatureHigh
        key='temperature'
        iconSize={iconSize}
        showText={showText}
        smallIcons={smallIcons}
        value={temperatureHighDisplayValue}
      />
    ),
  ]);

  const visibleIconsCount = G.ifElse(
    collapseOverflow,
    getVisibleItemIconsCount(containerSize.width, R.length(itemIcons)),
    R.length(itemIcons),
  );

  const visibleIcons = R.take(visibleIconsCount, itemIcons);
  const remainingIcons = R.drop(visibleIconsCount, itemIcons);
  const remainingIconsCount = R.length(remainingIcons);

  const handleOverflowClick = (event) => {
    if (G.isFunction(onOverflowClick)) onOverflowClick(event, remainingIcons);
  };

  return (
    <Flex
      gap={R.or(iconGap, 0)}
      width={R.or(width, '100%')}
      ref={G.ifElse(collapseOverflow, containerRef, null)}
      justifyContent={R.or(justifyContent, 'space-around')}
    >
      {visibleIcons}
      {
        R.gt(remainingIconsCount, 0) &&
        <Flex
          px='6px'
          border='0'
          height={24}
          as='button'
          type='button'
          fontSize={14}
          cursor='pointer'
          borderRadius='12px'
          color={overflowColor}
          bg={overflowBackground}
          justifyContent='center'
          onClick={handleOverflowClick}
          minWidth={OVERFLOW_BADGE_WIDTH}
          aria-label={G.getWindowLocale('titles:more-item-indicators', 'More Item Indicators')}
        >
          +{remainingIconsCount}
        </Flex>
      }
    </Flex>
  );
};
